import { CatalogStore, QuotaExceeded } from './CatalogStore';
import type { CatalogSort } from '../models/CatalogSort';
import { CATALOG_SORTS } from '../models/CatalogSort';
import type { ScoreItem } from '../models/ScoreItem';
import { BUDGET_BYTES, formatBytes } from '../storage/localStore';
import { el, escapeHtml } from '../dom';

type Alert =
  | { kind: 'offer'; item: ScoreItem }
  | { kind: 'failure'; item: ScoreItem; message: string }
  | { kind: 'delete'; item: ScoreItem }
  | { kind: 'quota'; message: string };

const ICONS = {
  sort: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M8 4v16M8 20l-3-3M8 20l3-3M16 20V4M16 4l-3 3M16 4l3 3"/></svg>',
  add: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
  search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="11" cy="11" r="6"/><path d="M20 20l-3.5-3.5"/></svg>',
  music: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 4v12.2A3.4 3.4 0 1 0 11 19V9.2l8-1.7V16.2A3.4 3.4 0 1 0 21 19V4.6z"/></svg>',
  pdf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M7 3h7l5 5v13H7z"/><path d="M14 3v5h5"/></svg>',
  image: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="4" y="5" width="16" height="14" rx="2"/><circle cx="9" cy="10" r="1.4"/><path d="M4 16l5-4 4 3 3-2 4 3"/></svg>',
};

/**
 * The Library tab: search, sort, import and delete, plus the tap-through to the
 * player. Mirrors Singline/Sources/Views/CatalogView.swift.
 */
export class CatalogView {
  private query = '';
  private sort: CatalogSort = 'newest';
  private alert: Alert | null = null;
  private sortOpen = false;
  private dragging = false;
  private readonly fileInput: HTMLInputElement;

  constructor(
    private readonly root: HTMLElement,
    private readonly store: CatalogStore,
    private readonly onPlay: (item: ScoreItem) => void,
  ) {
    this.fileInput = el('input', {
      class: 'hidden-input',
      type: 'file',
      multiple: 'multiple',
      accept: '.musicxml,.xml,.pdf,.png,.jpg,.jpeg,.heic,.webp,application/pdf,image/*,application/xml,text/xml',
    });
    this.fileInput.addEventListener('change', () => {
      const files = this.fileInput.files ? [...this.fileInput.files] : [];
      this.fileInput.value = '';
      void this.importFiles(files);
    });
    this.root.addEventListener('dragover', this.onDragOver);
    this.root.addEventListener('dragleave', this.onDragLeave);
    this.root.addEventListener('drop', this.onDrop);
    this.store.subscribe(() => this.render());
    this.render();
  }

  private render(): void {
    const { recognitionProgress, usageBytes } = this.store.snapshot();
    const visible = this.store.itemsMatching(this.query, this.sort);

    this.root.replaceChildren();
    this.root.className = `catalog tab-panel${this.dragging ? ' drop-target' : ''}`;

    const bar = el('div', { class: 'top-bar' });
    const sortBtn = el('button', { class: 'icon-button', type: 'button', 'aria-label': 'Sort' });
    sortBtn.innerHTML = ICONS.sort;
    sortBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      this.sortOpen = !this.sortOpen;
      this.render();
    });
    bar.append(sortBtn, el('h1', {}, 'Library'));
    const addBtn = el('button', { class: 'icon-button accent', type: 'button', 'aria-label': 'Import' });
    addBtn.innerHTML = ICONS.add;
    addBtn.addEventListener('click', () => this.fileInput.click());
    bar.append(addBtn);
    this.root.append(bar, this.fileInput);

    if (this.sortOpen) {
      const menu = el('div', { class: 'menu', role: 'menu' });
      menu.style.left = '12px';
      menu.style.top = '52px';
      for (const option of CATALOG_SORTS) {
        const button = el('button', {
          type: 'button',
          role: 'menuitemradio',
          'aria-checked': option.id === this.sort ? 'true' : 'false',
        }, option.title);
        button.addEventListener('click', () => {
          this.sort = option.id;
          this.sortOpen = false;
          this.render();
        });
        menu.append(button);
      }
      this.root.append(menu);
      const dismiss = () => {
        this.sortOpen = false;
        this.render();
      };
      window.setTimeout(() => document.addEventListener('click', dismiss, { once: true }), 0);
    }

    const search = el('label', { class: 'search' });
    search.innerHTML = ICONS.search;
    const input = el('input', {
      type: 'search',
      placeholder: 'Search',
      value: this.query,
      'aria-label': 'Search',
    });
    input.addEventListener('input', () => {
      this.query = input.value;
      this.render();
      const next = this.root.querySelector<HTMLInputElement>('input[type="search"]');
      next?.focus();
      if (next) next.selectionStart = next.selectionEnd = next.value.length;
    });
    search.append(input);
    this.root.append(search);

    const usage = el('div', { class: 'usage' });
    usage.innerHTML = `${escapeHtml(formatBytes(usageBytes))} of ${escapeHtml(formatBytes(BUDGET_BYTES))} used`;
    const meter = el('meter', {
      min: '0',
      max: String(BUDGET_BYTES),
      value: String(usageBytes),
      'aria-label': 'Library storage',
    });
    meter.value = usageBytes;
    usage.append(meter);
    this.root.append(usage);

    if (visible.length === 0) {
      this.root.append(this.emptyState());
    } else {
      const list = el('ul', { class: 'score-list' });
      for (const item of visible) {
        list.append(this.row(item, recognitionProgress[item.id]));
      }
      this.root.append(list);
    }

    if (this.alert) this.root.append(this.dialog(this.alert));
  }

  private row(item: ScoreItem, progressText: string | undefined): HTMLLIElement {
    const li = el('li');
    const button = el('button', { class: 'score-row', type: 'button' });
    const tile = el('div', { class: `tile ${item.kind}` });
    tile.innerHTML = item.kind === 'musicxml' ? ICONS.music : item.kind === 'pdf' ? ICONS.pdf : ICONS.image;
    const copy = el('div', { class: 'score-copy' });
    copy.innerHTML = `<strong>${escapeHtml(item.title)}</strong><span>${escapeHtml(formatDate(item.addedAt))}</span>`;
    button.append(tile, copy);
    if (item.kind !== 'musicxml') button.append(this.badge(item, progressText));
    button.addEventListener('click', () => this.open(item));
    button.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      this.alert = { kind: 'delete', item };
      this.render();
    });
    let timer: number | undefined;
    button.addEventListener('pointerdown', () => {
      timer = window.setTimeout(() => {
        this.alert = { kind: 'delete', item };
        this.render();
      }, 500);
    });
    button.addEventListener('pointerup', () => window.clearTimeout(timer));
    button.addEventListener('pointerleave', () => window.clearTimeout(timer));
    li.append(button);
    return li;
  }

  private badge(item: ScoreItem, progressText: string | undefined): HTMLElement {
    const state = item.recognition;
    if (state.state === 'pending') {
      const node = el('div', { class: 'badge pending' });
      node.innerHTML = `<span class="spinner"></span>${escapeHtml(progressText ?? 'recognizing…')}`;
      return node;
    }
    if (state.state === 'failed') {
      return el('div', { class: 'badge failed' }, 'error');
    }
    if (state.state === 'done') {
      return el('div', { class: 'badge done' }, 'recognized');
    }
    return el('div', { class: 'badge none' }, 'not recognized');
  }

  private emptyState(): HTMLElement {
    const searching = this.query.trim() !== '';
    const node = el('div', { class: 'empty' });
    node.innerHTML = `${searching ? ICONS.search : ICONS.music}
      <h2>${searching ? 'No Results' : 'No Sheet Music'}</h2>
      <p>${searching
        ? 'Try a different search.'
        : 'Tap “+” to add sheet music (MusicXML, PDF or a photo), or drop files here.'}</p>`;
    return node;
  }

  private dialog(alert: Alert): HTMLElement {
    const backdrop = el('div', { class: 'dialog-backdrop' });
    const box = el('div', { class: 'dialog', role: 'dialog', 'aria-modal': 'true' });
    const title = el('h2');
    const message = el('p');
    const actions = el('div', { class: 'dialog-actions' });
    const cancel = el('button', { type: 'button' }, 'Cancel');
    cancel.addEventListener('click', () => {
      this.alert = null;
      this.render();
    });
    const confirm = el('button', { type: 'button' });
    if (alert.kind === 'offer') {
      title.textContent = 'Not recognized yet';
      message.textContent = 'Convert this file to sheet music (MusicXML) so it can be played?';
      confirm.textContent = 'Recognize';
      confirm.addEventListener('click', () => {
        this.store.recognize(alert.item);
        this.alert = null;
        this.render();
      });
    } else if (alert.kind === 'failure') {
      title.textContent = 'Recognition failed';
      message.textContent = alert.message;
      confirm.textContent = 'Try Again';
      confirm.addEventListener('click', () => {
        this.store.recognize(alert.item);
        this.alert = null;
        this.render();
      });
    } else if (alert.kind === 'delete') {
      title.textContent = `Delete “${alert.item.title}”?`;
      message.textContent = 'The file is removed from the library.';
      confirm.textContent = 'Delete';
      confirm.classList.add('danger');
      confirm.addEventListener('click', () => {
        void this.store.delete(alert.item);
        this.alert = null;
      });
    } else {
      title.textContent = 'Library is full';
      message.textContent = alert.message;
      confirm.textContent = 'OK';
      confirm.addEventListener('click', () => {
        this.alert = null;
        this.render();
      });
      actions.append(confirm);
      box.append(title, message, actions);
      backdrop.append(box);
      backdrop.addEventListener('click', (event) => {
        if (event.target === backdrop) {
          this.alert = null;
          this.render();
        }
      });
      return backdrop;
    }
    actions.append(cancel, confirm);
    box.append(title, message, actions);
    backdrop.append(box);
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) {
        this.alert = null;
        this.render();
      }
    });
    return backdrop;
  }

  /**
   * MusicXML opens in the player; a PDF/image is a recognition source only, so it
   * either offers recognition, shows the failure, or does nothing while pending.
   */
  private open(item: ScoreItem): void {
    if (item.kind === 'musicxml') {
      this.onPlay(item);
      return;
    }
    switch (item.recognition.state) {
      case 'none':
        this.alert = { kind: 'offer', item };
        this.render();
        break;
      case 'failed':
        this.alert = { kind: 'failure', item, message: item.recognition.message };
        this.render();
        break;
      default:
        break;
    }
  }

  private async importFiles(files: File[]): Promise<void> {
    try {
      await this.store.importFiles(files);
    } catch (error) {
      const message = error instanceof QuotaExceeded
        ? error.message
        : error instanceof Error ? error.message : 'Could not import those files.';
      this.alert = { kind: 'quota', message };
      this.render();
    }
  }

  private onDragOver = (event: DragEvent): void => {
    if (![...event.dataTransfer?.types ?? []].includes('Files')) return;
    event.preventDefault();
    if (!this.dragging) {
      this.dragging = true;
      this.root.classList.add('drop-target');
    }
  };

  private onDragLeave = (event: DragEvent): void => {
    if (event.target !== this.root) return;
    this.dragging = false;
    this.root.classList.remove('drop-target');
  };

  private onDrop = (event: DragEvent): void => {
    event.preventDefault();
    this.dragging = false;
    const files = event.dataTransfer ? [...event.dataTransfer.files] : [];
    void this.importFiles(files);
    this.render();
  };
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}
